// src/App.jsx
import {
    createPortal,
    useEffect,
    lazy,
    Suspense,
    useRef,
    createContext,
    useState,
    useContext,
    useCallback
} from '@wordpress/element';
import Shortcode from './Shortcode';
import LoadingSpinner from './components/LoadingSpinner';
import { getExistingCheckout, getRetailerId, scrollToShop, consoleDebug } from './util';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ErrorBoundary } from '@sentry/react';
import ErrorComponent from './ErrorComponent';
import { categoryTypes, storageItemName } from './config';
import * as storage from './browserStorage';
import { ConfigContext } from './index.js';
import { useCart, CartRetailerContext } from './contexts/CartContext';
import { useRetailerData } from './hooks/dutchie-queries.js';
import { useCreateCart } from './hooks/dutchie-mutations.js';
import { Helmet } from 'react-helmet';
import Fallback from './Fallback.jsx';
import { useInitialURLParams } from './hooks/useInitialURLParams';

const CartSidebarIcon = lazy(() => import('./components/Cart/CartSidebarIcon'));
const CartSidebar = lazy(() => import('./components/Cart/CartSidebar'));
const LocationModal = lazy(() =>
    import('./components/LocationSelect/LocationModal')
);

export const RetailerIdContext = createContext(null);
export const ToggleSidebarContext = createContext(null);

const App = ({ shortcodeData, fallback }) => {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const initialParams = useInitialURLParams();
    const [currentParams, setCurrentParams] = useState(initialParams);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isCartReady, setIsCartReady] = useState(false);

    useEffect(() => {
        setCurrentParams(initialParams);
    }, [initialParams]);

    const [retailerId, setRetailerId] = useState(null);
    const [nonHeadlessRetailer, setNonHeadlessRetailer] = useState(storage.getItem('NON_HEADLESS_RETAILER'));
    const [shouldRenderCart, setShouldRenderCart] = useState(false);
    const [nonHeadlessRetailerData, setNonHeadlessRetailerData] = useState(null);
    const [sidebarToggle, setSidebarToggle] = useState(false);
    const [fallbackEnabled, setFallbackEnabled] = useState(false);
    const [ageGateVisible, setAgeGateVisible] = useState(true);
    const [locationRedirect, setLocationRedirect] = useState(false);

    const checkout = getExistingCheckout();

    const globalConfig = useContext(ConfigContext);
    const { retailers, nonHeadlessRetailers, locationGateConfig } = globalConfig;

    const { setRetailerId: setCartRetailerId } = useContext(CartRetailerContext);


    /**
     * Validate checkout data and retailerId
     */
    useEffect(() => {
        // Skip validation if we don't have retailers data yet
        if (!retailers) return;

        const checkout = getExistingCheckout();

        // Validate checkout data
        if (checkout && (!checkout.retailerId || !retailers[checkout.retailerId])) {
            // Invalid data detected, clear it
            storage.removeItem(storageItemName);

            // Force retailer selection
            setRetailerId(null);
            setCartRetailerId(null);
            setLocationRedirect(true);
            return;
        }

        // Additional validation for retailerId state that doesn't match retailers
        if (retailerId && retailers && !retailers[retailerId]) {
            setRetailerId(null);
            setCartRetailerId(null);
            setLocationRedirect(true);
        }
    }, [retailers, retailerId, setCartRetailerId]);

    /**
     * Attempt to recover from error state
     */
    useEffect(() => {
        // Recovery function to be called when errors occur
        const recoverFromError = () => {
            // Clear potentially corrupted data
            storage.removeItem(storageItemName);

            // Reset key state
            setRetailerId(null);
            setIsInitialized(false);

            // Retry initialization after a short delay
            setTimeout(() => {
                setIsInitialized(true);
            }, 500);
        };

        // Listen for errors that might indicate data issues
        window.addEventListener('unhandledrejection', (event) => {
            if (event.reason &&
                (event.reason.message?.includes('retailer') ||
                    event.reason.message?.includes('cart'))) {
                recoverFromError();
            }
        });

        return () => {
            window.removeEventListener('unhandledrejection', recoverFromError);
        };
    }, []);

    const { data: retailerData, isFetched: isFetchedRetailerData } = useRetailerData(retailerId);
    const cartCreatedRetailerId = useRef(null);
    const {
        cartData,
        isFetched: isFetchedCartData,
        isFetching: isFetchingCartData,
    } = useCart();
    const handleCreateCart = useCreateCart(retailerId);

    // Move initialization logic to a dedicated effect

    const hasRetailerShortcode = Object.values(shortcodeData).some(
        (data) =>
            data.shortcode === 'headlessproducts' &&
            data?.retailerShortcode?.providedId
    );

    const getPricingType = useCallback((retailerId) => {
        const currentRetailer = retailers[retailerId] || nonHeadlessRetailers.find(r => r.id === retailerId);
        return currentRetailer?.menuType === 'medical' ? 'MEDICAL' : 'RECREATIONAL';
    }, [retailers, nonHeadlessRetailers]);


    const checkForNonHeadlessRetailer = () => {
        const currentPath = window.location.pathname;
        const isLocationPage = /^\/location\//.test(currentPath);

        if (isLocationPage && !hasRetailerShortcode) {
            const currentURL = window.location.href;
            const matchingRetailer = Object.values(nonHeadlessRetailers).find(retailer =>
                currentURL.startsWith(retailer.non_headless_location_homepage)
            );

            if (matchingRetailer) {
                storage.setItem('NON_HEADLESS_RETAILER', matchingRetailer.location_name);
                storage.removeItem(storageItemName);
                setNonHeadlessRetailer(matchingRetailer.location_name);
                setNonHeadlessRetailerData(matchingRetailer);
            }
        }
    };

    let retailerShortcode;

    if (nonHeadlessRetailer) {
        document.body.classList.add('non-headless-retailer');
        retailerShortcode = null;
        storage.removeItem(storageItemName);
    } else {
        document.body.classList.remove('non-headless-retailer');
        retailerShortcode = Object.values(shortcodeData).find(
            (data) =>
                data.shortcode === 'headlessproducts' &&
                data?.retailerShortcode?.providedId
        )?.retailerShortcode?.providedId;
        storage.removeItem('NON_HEADLESS_RETAILER');
    }

    // Only render the cart sidebar for these shortcodes (and if its a headless retailer).
    useEffect(() => {
        const hasValidShortcode = Object.values(shortcodeData).find(
            (data) =>
                data.shortcode === 'singleproduct' ||
                data.shortcode === 'headlessproducts' ||
                data.shortcode === 'headlessmenu' ||
                data.shortcode === 'shopnavfull'
        );

        setShouldRenderCart(!nonHeadlessRetailer && hasValidShortcode);
    }, [shortcodeData, nonHeadlessRetailer]);

    // URL param queries
    const searchQuery = new URLSearchParams(location.search).get('search');
    const thcMin = new URLSearchParams(location.search).get('thcMin');
    const menuType = new URLSearchParams(location.search).get('menuType');

    // Construct the query string
    const queryParams = new URLSearchParams();
    if (searchQuery) queryParams.append('search', searchQuery);
    if (thcMin) queryParams.append('thcMin', thcMin);
    if (menuType) queryParams.append('menuType', menuType);

    // Convert query params to string and add '?' if there are any params
    const queryString = queryParams.toString();
    const queryStringWithQuestionMark = queryString ? `?${queryString}` : '';

    // If API has only one retailer or not.
    const isSingleRetailer = retailers && Object.keys(retailers).length === 1;

    /**
     * Navigate to menu immediately if only one shop.
     */
    useEffect(() => {
        if (
            isSingleRetailer &&
            params?.viewName === 'shop' &&
            typeof params.categoryName === 'undefined' &&
            retailers && Object.keys(retailers).length === 1
        ) {
            const singleRetailer = Object.values(retailers)[0];
            const targetSlug = singleRetailer.menuSlug;

            // IMPORTANT: Check if we're already at the target URL to prevent loops
            if (params.locationSlug === targetSlug) {
                // We're already at the correct URL, don't navigate
                return;
            }

            // Only navigate if we're at /shop/ without a location slug
            // or at a different location slug
            if (!params.locationSlug || params.locationSlug !== targetSlug) {
                // Only preserve specific query params
                const searchParams = new URLSearchParams(location.search);
                const preservedParams = new URLSearchParams();

                if (searchParams.get('search')) preservedParams.set('search', searchParams.get('search'));
                if (searchParams.get('thcMin')) preservedParams.set('thcMin', searchParams.get('thcMin'));
                if (searchParams.get('menuType')) preservedParams.set('menuType', searchParams.get('menuType'));

                const queryString = preservedParams.toString();
                const queryStringWithQuestionMark = queryString ? `?${queryString}` : '';

                navigate(
                    `/shop/${targetSlug}${queryStringWithQuestionMark}`,
                    {
                        state: { setFilters: false },
                    }
                );
            }
        }
    }, [isSingleRetailer, params?.viewName, params?.locationSlug, params?.categoryName, retailers, location.search, navigate]);

    /**
     * Check for age gate session to force rendering if not passed.
     */
    useEffect(() => {
        if (
            locationGateConfig &&
            locationGateConfig.enableAgeGate &&
            storage.getItem('HEADLESS_AGE_GATE_PASSED') !== 'true'
        ) {
            setAgeGateVisible(true);
        } else {
            // Explicitly set to false when age gate is passed
            setAgeGateVisible(false);
        }
    }, [locationGateConfig, retailerId]);

    /**
     * Initialize the app.
     */
    useEffect(() => {
        const initializeRetailer = async () => {
            // Skip if already initialized with same parameters
            if (isInitialized && retailerId) {
                const currentPath = window.location.pathname;
                const expectedPath = `/shop/${retailers[retailerId]?.menuSlug}`;

                // If we're on the correct path, no need to reinitialize
                if (currentPath.startsWith(expectedPath)) {
                    return;
                }
            }

            // Check for non-headless retailer
            if (hasRetailerShortcode && nonHeadlessRetailer) {
                storage.removeItem('NON_HEADLESS_RETAILER');
                setNonHeadlessRetailer(null);
            } else {
                checkForNonHeadlessRetailer();
            }

            // Determine the retailer ID
            let newRetailerId = null;

            // Priority 1: From shortcode
            if (hasRetailerShortcode) {
                const retailerShortcode = Object.values(shortcodeData).find(
                    (data) =>
                        data.shortcode === 'headlessproducts' &&
                        data?.retailerShortcode?.providedId
                )?.retailerShortcode?.providedId;

                if (retailerShortcode) {
                    newRetailerId = retailerShortcode;
                }
            }

            // Priority 2: From URL params
            if (!newRetailerId && params?.locationSlug && retailers) {
                const retailerMatch = Object.values(retailers).find(
                    (retailer) => retailer.menuSlug === params.locationSlug
                );
                if (retailerMatch) {
                    newRetailerId = retailerMatch.id;
                }
            }

            // Priority 3: From existing checkout
            if (!newRetailerId) {
                const existingCheckout = getExistingCheckout();
                if (existingCheckout?.retailerId && retailers[existingCheckout.retailerId]) {
                    newRetailerId = existingCheckout.retailerId;
                }
            }

            // Priority 4: Single retailer
            if (!newRetailerId && isSingleRetailer && retailers) {
                newRetailerId = Object.values(retailers)[0].id;
            }

            // Only update if we have a new valid retailer ID
            if (newRetailerId && newRetailerId !== retailerId) {
                setRetailerId(newRetailerId);
                setCartRetailerId(newRetailerId);
                setFallbackEnabled(fallback[newRetailerId]?.enabled || false);

                // Clear the cart creation ref to allow new cart
                cartCreatedRetailerId.current = null;
            }

            // Mark as initialized
            if (!isInitialized) {
                setIsInitialized(true);
            }
        };

        // Only run if we have retailers data
        if (retailers && Object.keys(retailers).length > 0) {
            initializeRetailer();
        }
    }, [
        retailers,
        params?.locationSlug,
        hasRetailerShortcode,
        shortcodeData,
        nonHeadlessRetailer,
        isSingleRetailer,
        // Don't include retailerId to prevent loops
    ]);

    // Separate effect for cart creation - only runs when retailerId is set/changed
    useEffect(() => {
        if (!retailerId) {
            setIsCartReady(false);
            return;
        }

        if (cartCreatedRetailerId.current === retailerId) {
            setIsCartReady(true);
            return;
        }

        const existingCheckout = getExistingCheckout();

        if (!existingCheckout && !handleCreateCart.isPending) {
            consoleDebug('Creating new cart for retailer:', retailerId);
            cartCreatedRetailerId.current = retailerId;
            handleCreateCart.mutate({
                variables: {
                    retailerId,
                    orderType: 'PICKUP',
                    pricingType: getPricingType(retailerId),
                }
            }).then(() => {
                setIsCartReady(true);
            });
        } else if (existingCheckout && existingCheckout.retailerId === retailerId) {
            setIsCartReady(true);
        } else if (existingCheckout && existingCheckout.retailerId !== retailerId) {
            consoleDebug('Retailer changed, creating new cart:', retailerId);
            storage.removeItem(storageItemName);
            cartCreatedRetailerId.current = retailerId;
            handleCreateCart.mutate({
                variables: {
                    retailerId,
                    orderType: 'PICKUP',
                    pricingType: getPricingType(retailerId),
                }
            }).then(() => {
                setIsCartReady(true);
            });
        }
    }, [retailerId, handleCreateCart.isPending, getPricingType]);

    useEffect(() => {
        if (!isInitialized || !retailerId || !retailers[retailerId]) return;

        const currentMenuSlug = retailers[retailerId]?.menuSlug;
        if (!currentMenuSlug) return;

        // Get ONLY the query parameters we want to preserve
        const searchParams = new URLSearchParams(location.search);
        const preservedParams = new URLSearchParams();

        // Define route-based parameters that should NOT be preserved in query string
        const routeBasedParams = ['category', 'brand', 'brandIds', 'subcategory', 'effects', 'tags', 'specials', 'staff_picks'];

        // Only preserve non-route parameters
        searchParams.forEach((value, key) => {
            if (!routeBasedParams.includes(key)) {
                // Only preserve search, THC/CBD/price ranges, and menuType
                if (['search', 'thcMin', 'thcMax', 'cbdMin', 'cbdMax', 'priceMin', 'priceMax', 'menuType'].includes(key)) {
                    preservedParams.set(key, value);
                }
            }
        });

        const queryString = preservedParams.toString();
        const queryStringWithQuestionMark = queryString ? `?${queryString}` : '';

        // Handle category-type paths that need retailer slug
        const categoryTypes = ['brand', 'category', 'tags', 'effect', 'specials', 'subcategory'];
        const isValidCategoryPath = categoryTypes.includes(params?.locationSlug);

        if (params?.viewName === 'shop' && isValidCategoryPath && !params?.categoryType) {
            consoleDebug('Direct category path detected, redirecting');
            navigate(`/shop/${currentMenuSlug}/${params.locationSlug}/${queryStringWithQuestionMark}`, {
                replace: true
            });
            return;
        }

        // Handle base /shop/ redirects
        if (params?.viewName === 'shop' && (!params?.locationSlug || params.locationSlug === 'undefined')) {
            navigate(`/shop/${currentMenuSlug}/${queryStringWithQuestionMark}`, {
                state: { setFilters: false },
                replace: true
            });
            return;
        }
    }, [isInitialized, retailerId, retailers, params, location.search, navigate]);


    /**
     * Debug retailer data.
     */
    const retailerDataLogged = useRef(false);
    const cartDataLogged = useRef(false);

    useEffect(() => {
        // Log retailer data when available and not logged yet
        if (isFetchedRetailerData && retailerData && !retailerDataLogged.current) {
            consoleDebug('🏪 Retailer Data Loaded:', {
                id: retailerData.id,
                name: retailerData.name,
                location: {
                    city: retailerData.address?.city,
                    state: retailerData.address?.state
                },
                menuType: retailerData.menuType,
                status: retailerData.status,
                fullObject: retailerData
            });
            retailerDataLogged.current = true;
        }

        // Log cart data when available and not logged yet
        if (isFetchedCartData && cartData && !cartDataLogged.current) {
            consoleDebug('🛒 Cart Data Loaded:', {
                id: cartData.id,
                itemCount: cartData.items?.length || 0,
                total: cartData.total,
                orderType: cartData.orderType,
                items: cartData.items?.map(item => ({
                    id: item.id,
                    name: item.product?.name,
                    quantity: item.quantity
                })),
                fullObject: cartData
            });
            cartDataLogged.current = true;
        }
    }, [retailerData, cartData, isFetchedRetailerData, isFetchedCartData]);

    if (!isInitialized) {
        return <LoadingSpinner />;
    }

    // emergency fallback.
    if (fallbackEnabled && retailerId) {
        return (
            <>
                {Object.entries(shortcodeData).map(([rootId, dataset], i) => {
                    let root = document.getElementById(rootId);

                    if (i === 0) {
                        return (
                            <Fallback
                                key={rootId}
                                rootId={rootId}
                                shortcodeData={dataset}
                                script={fallback[retailerId].script}
                            />
                        );
                    } else {
                        return createPortal(
                            <Fallback
                                key={rootId}
                                rootId={rootId}
                                shortcodeData={dataset}
                                script={fallback[retailerId].script}
                            />,
                            root
                        );
                    }
                })}
            </>
        );
    }
    // if there's retailers data but no retailer ID set, it means a location has to be chosen.
    else if (
        ((retailers &&
            (!retailerId || (retailerId && storage.getItem('HEADLESS_AGE_GATE_PASSED') !== 'true')) &&
            Object.keys(retailers).length > 1 &&
            !fallbackEnabled) ||
            (locationRedirect && !retailerId)) &&
        !nonHeadlessRetailer
    ) {
        // Removed dependency on ageGateVisible for showing location modal when retailerId is null
        return createPortal(
            <ErrorBoundary
                fallback={(props) =>
                    <ErrorComponent
                        {...props}
                        componentName="LocationModal"
                        parentName="App"
                    />
                }
            >
                <Suspense fallback={<LoadingSpinner />}>
                    <Helmet>
                        <link rel="canonical" href={window.location.href} />
                    </Helmet>
                    <LocationModal
                        retailers={retailers}
                        retailerId={retailerId}
                        setRetailerId={setRetailerId}
                        setNonHeadlessRetailer={setNonHeadlessRetailer}
                        nonHeadlessRetailerData={nonHeadlessRetailerData}
                        setNonHeadlessRetailerData={setNonHeadlessRetailerData}
                        ageGateVisible={!retailerId || storage.getItem('HEADLESS_AGE_GATE_PASSED') !== 'true'}
                        setAgeGateVisible={setAgeGateVisible}
                        locationRedirect={locationRedirect}
                        setLocationRedirect={setLocationRedirect}
                    />
                </Suspense>
            </ErrorBoundary>,
            document.body
        );
    }
    // if there's retailers data and a retailer ID set, we're good to load the main app. 
    else if ((isFetchedRetailerData && (isFetchedCartData || !getExistingCheckout()) && !fallbackEnabled && isCartReady) || nonHeadlessRetailer) {
        return (
            <>
                <Helmet>
                    <link rel="canonical" href={window.location.href} />
                </Helmet>
                <RetailerIdContext.Provider value={retailerId}>
                    <ToggleSidebarContext.Provider
                        value={{ sidebarToggle, setSidebarToggle }}
                    >
                        <ErrorBoundary
                            fallback={(props) =>
                                <ErrorComponent
                                    {...props}
                                    componentName="Shortcode"
                                    parentName="App"
                                />
                            }
                        >
                            {Object.entries(shortcodeData).map(
                                ([rootId, dataset], i) => {
                                    let root = document.getElementById(rootId);

                                    if (i === 0) {
                                        return (
                                            <Shortcode
                                                key={rootId}
                                                rootId={rootId}
                                                shortcodeData={dataset}
                                                setRetailerId={setRetailerId}
                                                nonHeadlessRetailer={nonHeadlessRetailer}
                                                setNonHeadlessRetailer={setNonHeadlessRetailer}
                                                forcedOrderType={initialParams}
                                                locationGateConfig={locationGateConfig}
                                                ageGateVisible={ageGateVisible}
                                                setAgeGateVisible={setAgeGateVisible}
                                                locationRedirect={locationRedirect}
                                                setLocationRedirect={setLocationRedirect}
                                                nonHeadlessRetailerData={nonHeadlessRetailerData}
                                                setNonHeadlessRetailerData={setNonHeadlessRetailerData}
                                                retailerId={retailerId}
                                                retailers={retailers}
                                                fallbackEnabled={fallbackEnabled}
                                            />
                                        );
                                    } else {
                                        return createPortal(
                                            <Shortcode
                                                key={rootId}
                                                rootId={rootId}
                                                shortcodeData={dataset}
                                                setRetailerId={setRetailerId}
                                                nonHeadlessRetailer={nonHeadlessRetailer}
                                                setNonHeadlessRetailer={setNonHeadlessRetailer}
                                                forcedOrderType={initialParams}
                                                locationGateConfig={locationGateConfig}
                                                ageGateVisible={ageGateVisible}
                                                setAgeGateVisible={setAgeGateVisible}
                                                locationRedirect={locationRedirect}
                                                setLocationRedirect={setLocationRedirect}
                                                nonHeadlessRetailerData={nonHeadlessRetailerData}
                                                setNonHeadlessRetailerData={setNonHeadlessRetailerData}
                                                retailerId={retailerId}
                                                retailers={retailers}
                                                fallbackEnabled={fallbackEnabled}
                                            />,
                                            root
                                        );
                                    }
                                }
                            )}
                            {shouldRenderCart && createPortal(
                                <ErrorBoundary
                                    fallback={(props) =>
                                        <ErrorComponent
                                            {...props}
                                            componentName="CartSidebar"
                                            parentName="App"
                                        />
                                    }
                                >
                                    <Suspense fallback={<LoadingSpinner />}>
                                        <CartSidebarIcon fixedCart={true} />
                                        <CartSidebar
                                            fixedCart={true}
                                            forcedOrderType={initialParams}
                                        />
                                    </Suspense>
                                </ErrorBoundary>,
                                document.body
                            )}
                        </ErrorBoundary>
                    </ToggleSidebarContext.Provider>
                </RetailerIdContext.Provider>
            </>
        );
    }

    return <LoadingSpinner />;
};

export default App;
